import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FamilyToggle } from "../components/FamilyToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ClipboardText, Sparkle, Trash, Clock, UsersThree, Stack, MagnifyingGlass, FilePlus, ArrowSquareOut, LinkSimple } from "@phosphor-icons/react";
import { DataResidencyNote } from "../components/DataResidencyNote";

const KIND_BADGE = {
  process: "border-sky-300 bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
  plan: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  procedure: "border-indigo-300 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300",
  program: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-950/40 dark:text-fuchsia-300",
  policy: "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
};
const CFR_LABEL = { sole: "Sole", shared_both: "Shared", split: "Split", not_applicable: "N/A", unspecified: "—" };
const STATUS = [["planned", "Planned"], ["in_progress", "In progress"], ["complete", "Complete"]];
const okey = (o) => `${o.standard_id}|${o.req_number}|${o.subpart}`;

export default function RequiredDocs() {
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [saved, setSaved] = useState([]);
  const [docs, setDocs] = useState([]);
  const [authoring, setAuthoring] = useState(null);
  const [sel, setSel] = useState({});           // okey -> obligation
  const [q, setQ] = useState("");
  const [sp] = useSearchParams();
  const fam = sp.get("family") || "all";
  const [timedOnly, setTimedOnly] = useState(false);
  const [blendOpen, setBlendOpen] = useState(false);
  const [blend, setBlend] = useState({ title: "", doc_kind: "procedure" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/required-docs/scan").then((r) => setScan(r.data)).catch(() => setScan([]));
    api.get("/required-docs").then((r) => setSaved(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const authorDoc = async (d) => {
    setAuthoring(d.id);
    try {
      const { data } = await api.post(`/required-docs/${d.id}/create-document`);
      toast.success(data.already_linked ? "Already linked to a document" : "Document drafted with applicability & coordination header");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to author document."); }
    finally { setAuthoring(null); }
  };
  const linkExisting = async (d, documentId) => {
    if (!documentId) return;
    try { await api.post(`/required-docs/${d.id}/link-document`, { document_id: documentId }); toast.success("Linked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unlink = async (d) => {
    try { await api.post(`/required-docs/${d.id}/unlink-document`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let rows = scan || [];
    if (fam !== "all") rows = rows.filter((r) => r.track === fam);
    if (timedOnly) rows = rows.filter((r) => r.timed);
    if (q.trim()) {
      const s = q.toLowerCase();
      rows = rows.filter((r) => `${r.standard_id} ${r.req_number} ${r.subpart} ${r.snippet}`.toLowerCase().includes(s));
    }
    return rows;
  }, [scan, fam, timedOnly, q]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) (g[r.standard_id] = g[r.standard_id] || []).push(r);
    return Object.entries(g);
  }, [filtered]);

  const selArr = Object.values(sel);
  const toggle = (o) => setSel((s) => { const n = { ...s }; if (n[okey(o)]) delete n[okey(o)]; else n[okey(o)] = o; return n; });
  const clearSel = () => setSel({});

  const toItem = (list, title, kind) => ({
    title, doc_kind: kind || (list.length === 1 ? list[0].doc_kind : "procedure"),
    standards: [...new Set(list.map((o) => o.standard_id))],
    requirement_ids: [...new Set(list.map((o) => o.requirement_id))],
    obligations: list.map((o) => ({ standard_id: o.standard_id, req_number: o.req_number, subpart: o.subpart,
      doc_kind: o.doc_kind, snippet: o.snippet, timed: o.timed, timing_note: o.timing_note })),
    timed: list.some((o) => o.timed),
    timing_note: list.map((o) => o.timing_note).filter(Boolean).join("; "),
  });

  const addIndividual = async () => {
    if (!selArr.length) return;
    setBusy(true);
    try {
      const items = selArr.map((o) => toItem([o], `${o.standard_id} ${o.req_number}${o.subpart ? "." + o.subpart : ""} ${o.doc_kind}`, o.doc_kind));
      const { data } = await api.post("/required-docs/bulk", { items });
      toast.success(`Added ${data.created} required document(s)`);
      clearSel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to add."); }
    finally { setBusy(false); }
  };

  const doBlend = async () => {
    if (!blend.title.trim()) return toast.error("Give the blended document a title");
    setBusy(true);
    try {
      await api.post("/required-docs", { ...toItem(selArr, blend.title, blend.doc_kind), blend: true });
      toast.success("Blended document created");
      setBlendOpen(false); setBlend({ title: "", doc_kind: "procedure" }); clearSel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  const setStatus = async (d, status) => {
    try { const { data } = await api.put(`/required-docs/${d.id}`, { ...d, status }); setSaved((cur) => cur.map((x) => (x.id === d.id ? data : x))); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.title}"?`)) return;
    try { await api.delete(`/required-docs/${d.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="required-docs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl text-[#071B3E] dark:text-white flex items-center gap-2">
            <ClipboardText size={26} weight="fill" className="text-indigo-700" />Required Documents
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            ClaroShield scans your <strong>applicable</strong> requirements (registration + assets + CFR) for obligations to
            <em> implement one or more documented process(es), plans, procedures, or programs</em>. Select obligations and blend
            them into deliverables, or keep one document per standard/requirement. Timed obligations show their tracker deadline.
          </p>
        </div>
        <FamilyToggle testid="required-docs-family-toggle" />
      </div>

      <DataResidencyNote className="mt-4" />

      {/* Saved list */}
      <div className="mt-6 border border-border" data-testid="required-docs-saved">
        <div className="border-b border-border bg-secondary/40 px-4 py-2.5 font-display font-bold text-sm">My required documents ({saved.length})</div>
        {saved.length === 0 ? (
          <div className="px-4 py-5 text-sm text-muted-foreground" data-testid="required-docs-empty">Nothing planned yet — select obligations below and add them.</div>
        ) : (
          <div className="divide-y divide-border">
            {saved.map((d) => (
              <div key={d.id} data-testid={`saved-doc-${d.id}`} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                <span className={`text-[10px] uppercase font-bold border px-1.5 py-0.5 shrink-0 ${KIND_BADGE[d.doc_kind] || ""}`}>{d.doc_kind}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                    {d.title}
                    {d.blend && <span className="text-[10px] font-bold border border-violet-300 bg-violet-50 text-violet-700 px-1.5 py-0.5 flex items-center gap-1"><Stack size={11} />Blended</span>}
                    {d.timed && <span className="text-[10px] font-bold border border-orange-300 bg-orange-50 text-orange-700 px-1.5 py-0.5 flex items-center gap-1"><Clock size={11} />Timed</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Covers: {(d.standards || []).join(", ") || "—"} · {(d.requirement_ids || []).length} requirement(s){d.timing_note ? ` · ${d.timing_note}` : ""}
                  </div>
                  {d.linked_document_id ? (
                    <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid={`linked-doc-${d.id}`}>
                      <button onClick={() => navigate("/documents")} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 border border-emerald-300 px-1.5 py-0.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/40">
                        <ArrowSquareOut size={11} />Open: {d.linked_document_title || "document"}
                      </button>
                      {canEdit && <button data-testid={`unlink-${d.id}`} onClick={() => unlink(d)} className="text-[11px] text-muted-foreground hover:text-red-600">unlink</button>}
                    </div>
                  ) : canEdit && (
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <button data-testid={`author-doc-${d.id}`} onClick={() => authorDoc(d)} disabled={authoring === d.id}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 disabled:opacity-50">
                        <FilePlus size={11} />{authoring === d.id ? "Drafting…" : "Author document"}
                      </button>
                      <span className="text-[11px] text-muted-foreground">or</span>
                      <div className="inline-flex items-center gap-1">
                        <LinkSimple size={11} className="text-muted-foreground" />
                        <select data-testid={`link-existing-${d.id}`} defaultValue="" onChange={(e) => linkExisting(d, e.target.value)}
                          className="border border-border bg-background px-1.5 py-0.5 text-[11px] rounded-none max-w-[180px]">
                          <option value="">link existing…</option>
                          {docs.map((doc) => <option key={doc.id} value={doc.id}>{doc.title}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2 shrink-0">
                    <select data-testid={`saved-status-${d.id}`} value={d.status} onChange={(e) => setStatus(d, e.target.value)}
                      className="border border-border bg-background px-2 py-1 text-xs rounded-none">
                      {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button data-testid={`saved-delete-${d.id}`} onClick={() => remove(d)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selection toolbar */}
      {canEdit && (
        <div className="mt-6 flex items-center gap-2 flex-wrap sticky top-0 z-10 bg-background/95 backdrop-blur py-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
            <Input data-testid="scan-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search obligations…" className="rounded-none pl-7 h-8 w-56" />
          </div>
          <label className="text-xs flex items-center gap-1.5 cursor-pointer"><input data-testid="scan-timed-only" type="checkbox" checked={timedOnly} onChange={(e) => setTimedOnly(e.target.checked)} />Timed only</label>
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground" data-testid="scan-sel-count">{selArr.length} selected</span>
          <Button data-testid="add-individual-btn" onClick={addIndividual} disabled={!selArr.length || busy} size="sm" variant="outline" className="rounded-none h-8">Add as individual</Button>
          <Button data-testid="blend-btn" onClick={() => setBlendOpen(true)} disabled={selArr.length < 2 || busy} size="sm" className="rounded-none h-8 bg-[#071B3E] hover:bg-[#0c2a5e]"><Stack size={14} className="mr-1" />Blend selected</Button>
        </div>
      )}

      {/* Scan list */}
      <div className="border border-border" data-testid="scan-list">
        <div className="border-b border-border bg-secondary/40 px-4 py-2.5 font-display font-bold text-sm flex items-center gap-2">
          <Sparkle size={15} weight="fill" className="text-indigo-600" />Detected documentation obligations {scan ? `(${filtered.length})` : ""}
        </div>
        {scan === null ? (
          <div className="px-4 py-6 text-sm text-muted-foreground">Scanning applicable requirements…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-6 text-sm text-muted-foreground" data-testid="scan-empty">No documentation obligations detected for the current filters.</div>
        ) : (
          <div className="divide-y divide-border">
            {grouped.map(([sid, rows]) => (
              <div key={sid}>
                <div className="px-4 py-1.5 bg-secondary/20 text-xs font-bold font-mono">{sid}</div>
                {rows.map((o) => (
                  <label key={okey(o)} data-testid={`obligation-${o.standard_id}-${o.req_number}-${o.subpart || "main"}`}
                    className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer hover:bg-secondary/20 ${o.already_covered ? "opacity-60" : ""}`}>
                    <input type="checkbox" data-testid={`obligation-check-${o.standard_id}-${o.req_number}-${o.subpart || "main"}`}
                      className="mt-1" checked={!!sel[okey(o)]} disabled={o.already_covered} onChange={() => toggle(o)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold">{o.req_number}{o.subpart ? `.${o.subpart}` : ""}</span>
                        <span className={`text-[10px] uppercase font-bold border px-1.5 py-0.5 ${KIND_BADGE[o.doc_kind] || ""}`}>{o.doc_kind}</span>
                        {o.timed && (
                          <span className="text-[10px] font-bold border border-orange-300 bg-orange-50 text-orange-700 px-1.5 py-0.5 flex items-center gap-1">
                            <Clock size={10} />{o.tracker?.compliance_date ? `Due ${o.tracker.compliance_date}` : (o.timing_note || "Timed")}
                            {o.tracker?.frequency_label ? ` · ${o.tracker.frequency_label}` : ""}
                          </span>
                        )}
                        {o.cfr_type && CFR_LABEL[o.cfr_type] && (
                          <span className="text-[10px] font-bold border border-violet-300 bg-violet-50 text-violet-700 px-1.5 py-0.5 flex items-center gap-1"><UsersThree size={10} />CFR · {CFR_LABEL[o.cfr_type]}</span>
                        )}
                        {o.already_covered && <span className="text-[10px] font-bold text-emerald-700">✓ already planned</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{o.snippet}</p>
                    </div>
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={blendOpen} onOpenChange={setBlendOpen}>
        <DialogContent className="rounded-none" data-testid="blend-dialog">
          <DialogHeader><DialogTitle>Blend {selArr.length} obligations into one document</DialogTitle>
            <DialogDescription>Combine related obligations (e.g. CIP-010 change management + CIP-007 patching) into a single documented deliverable.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Document title</Label><Input data-testid="blend-title" value={blend.title} onChange={(e) => setBlend({ ...blend, title: e.target.value })} placeholder="e.g. Change & Configuration Management Procedure" className="rounded-none mt-1" /></div>
            <div><Label>Document kind</Label>
              <select data-testid="blend-kind" value={blend.doc_kind} onChange={(e) => setBlend({ ...blend, doc_kind: e.target.value })} className="w-full border border-border bg-background px-2 py-2 text-sm rounded-none mt-1">
                {["procedure", "process", "plan", "program", "policy"].map((k) => <option key={k} value={k}>{k}</option>)}
              </select></div>
            <div className="text-xs text-muted-foreground">Covers: {[...new Set(selArr.map((o) => o.standard_id))].join(", ")}</div>
            <Button data-testid="blend-confirm" onClick={doBlend} disabled={busy} className="rounded-none w-full">Create blended document</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
